import { API_CONFIG } from '../config/api';

export interface Staff {
  id: number;
  name: string;
  email: string;
  phone?: string;
  position: string;
  department?: string;
  salary?: number;
  hire_date?: string;
  status: 'active' | 'inactive';
  created_at?: string;
  updated_at?: string;
}

export interface CreateStaffData {
  name: string;
  email: string;
  phone?: string;
  position: string;
  department?: string;
  salary?: number;
  hire_date?: string;
  status: 'active' | 'inactive';
}

export interface UpdateStaffData extends CreateStaffData {
  id: number;
}

export const getAllStaff = async (): Promise<Staff[]> => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required. Please sign in again.');
    }

    const response = await fetch(`${API_CONFIG.BASE_URL}/staff`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch staff:', response.status, errorText);
      
      if (response.status === 401 || response.status === 403) {
        throw new Error('Authentication failed. Please sign in again.');
      } else if (response.status === 404) {
        throw new Error('Staff endpoint not found. Please check your configuration.');
      } else {
        throw new Error(`Failed to fetch staff: ${response.status}`);
      }
    }

    const result = await response.json();
    console.log('Raw API response for staff:', result);
    
    // Handle different response formats
    if (result.data && Array.isArray(result.data)) {
      return result.data;
    } else if (Array.isArray(result)) {
      return result;
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching staff:', error);
    throw error;
  }
};

export const getStaffById = async (id: number): Promise<Staff> => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required. Please sign in again.');
    }

    const response = await fetch(`${API_CONFIG.BASE_URL}/staff/${id}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch staff member:', response.status, errorText);
      
      if (response.status === 401 || response.status === 403) {
        throw new Error('Authentication failed. Please sign in again.');
      } else if (response.status === 404) {
        throw new Error('Staff member not found.');
      } else {
        throw new Error(`Failed to fetch staff member: ${response.status}`);
      }
    }

    const result = await response.json();
    
    // Handle different response formats
    if (result.data) {
      return result.data;
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching staff member:', error);
    throw error;
  }
};

export const createStaff = async (staffData: CreateStaffData): Promise<Staff> => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required. Please sign in again.');
    }

    const response = await fetch(`${API_CONFIG.BASE_URL}/staff`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(staffData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to create staff member:', response.status, errorText);
      
      let errorMessage = 'Failed to create staff member';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    const result = await response.json();
    
    // Handle different response formats
    if (result.data) {
      return result.data;
    }
    
    return result;
  } catch (error) {
    console.error('Error creating staff member:', error);
    throw error;
  }
};

export const updateStaff = async (staffData: UpdateStaffData): Promise<Staff> => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required. Please sign in again.');
    }

    const response = await fetch(`${API_CONFIG.BASE_URL}/staff/${staffData.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(staffData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to update staff member:', response.status, errorText);
      
      let errorMessage = 'Failed to update staff member';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    const result = await response.json();
    
    // Handle different response formats
    if (result.data) {
      return result.data;
    }
    
    return result;
  } catch (error) {
    console.error('Error updating staff member:', error);
    throw error;
  }
};

export const deleteStaff = async (id: number): Promise<void> => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required. Please sign in again.');
    }

    const response = await fetch(`${API_CONFIG.BASE_URL}/staff/${id}`, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to delete staff member:', response.status, errorText);
      
      let errorMessage = 'Failed to delete staff member';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    console.log('Staff member deleted successfully');
  } catch (error) {
    console.error('Error deleting staff member:', error);
    throw error;
  }
};
